/**
 * Point-source drip water model. See docs/specs/drip-water-model.md.
 *
 * The engine models water as depth (inches) over an area, which quietly assumes
 * the water is applied to, drawn from, and stored in the *same* area — true for
 * spray/rotor, false for drip, which wets only a fraction of the plant's canopy.
 * Working the soil balance in canopy-referenced inches, that whole divergence
 * collapses into a single number: the wetted fraction f = wettedArea /
 * canopyArea. The managed reservoir shrinks by f (so it depletes faster → short,
 * frequent runs) and rain reaching the concentrated roots is credited × f; ET,
 * per-run delivery/runtime, and the safety caps are unchanged. For non-drip
 * zones f = 1, so every f-scaling below is a no-op and fully backwards
 * compatible.
 */
import { IN_PER_GAL_PER_SQFT } from './tables.js';
/** Whether a zone is modeled as point-source drip. */
export function isDripModeled(zone) {
    return zone.dripParams !== undefined;
}
/**
 * The zone's wetted fraction f (0 < f ≤ 1). Drip zones store it; everything else
 * is 1 (the whole area is wetted). An out-of-range or missing value falls back
 * to 1 so a malformed drip zone degrades to the legacy model rather than
 * producing nonsense.
 */
export function wettedFraction(zone) {
    const f = zone.dripParams?.wettedFraction;
    return f !== undefined && f > 0 && f <= 1 ? f : 1;
}
/**
 * Effective precipitation rate (inches/hour, averaged over the canopy) implied
 * by an emitter layout: total emitter flow spread across the canopy area.
 * = 1.604 × emitterCount × emitterGph / canopyAreaSqFt (1.604 = inches of depth
 * one gallon makes over one ft²). Returns 0 for a non-positive canopy area.
 */
export function derivedDripPrecipRate(emitterCount, emitterGph, canopyAreaSqFt) {
    if (canopyAreaSqFt <= 0)
        return 0;
    return (IN_PER_GAL_PER_SQFT * emitterCount * emitterGph) / canopyAreaSqFt;
}
//# sourceMappingURL=drip.js.map